import styled from '@emotion/styled';
import { useNavigate, useLocation } from 'react-router-dom';
import { useConsumerT } from '../translations/landingConsumer';
import ConsumerBackButton from '../components/ConsumerBackButton';
import { parcelList } from '../../config/parcelList';

export default function SelectParcelScreenContainer() {
  const t = useConsumerT();
  const navigate = useNavigate();
  const location = useLocation();
  const isDepositTutorial = location.pathname.includes('/deposit/tutorial');
  const isSendBackTutorial = location.pathname.includes('/sendback/tutorial');
  const action = location.pathname.endsWith('retour') || isSendBackTutorial ? 'retour' : 'reception';
  const returnTo = location.state?.returnTo || '/';

  function handleParcelClick(parcel) {
    const params = new URLSearchParams();
    params.set('type', parcel.parcelType);
    if (parcel.parcelVersion.length > 0) {
      params.set('version', parcel.parcelVersion.join(','));
    }
    let targetPath;
    if (isDepositTutorial) {
      targetPath = `/deposit/tutorial?${params}`;
    } else if (isSendBackTutorial) {
      targetPath = `/sendback/tutorial?${params}`;
    } else if (action === 'reception') {
      targetPath = `/deposit?${params}`;
    } else {
      targetPath = `/sendback?${params}`;
    }
    navigate(targetPath, {
      state: {
        returnTo: location.pathname,
        finalReturnTo: returnTo,
      }
    });
  }

  return (
    <Page>
      <TopBar>
        <ConsumerBackButton
          onBack={() => navigate(returnTo)}
          variant="light"
          label={t(`selectParcel.${action}-title`)}
          ariaLabel={t('common.back')}
        />
      </TopBar>

      <Subtitle>{t(`selectParcel.${action}-subtitle`)}</Subtitle>

      <Grid>
        {parcelList.map((parcel, i) => (
          <ParcelCard key={i}>
            <ImageBox>
              <img
                src={`${process.env.PUBLIC_URL}/img/${parcel.imageName}`}
                alt={parcel.description}
              />
            </ImageBox>
            <CardDescription>{parcel.description}</CardDescription>
            <TutorialBtn type="button" onClick={() => handleParcelClick(parcel)}>
              {t('selectParcel.cta-tutorial')}
            </TutorialBtn>
          </ParcelCard>
        ))}
      </Grid>
    </Page>
  );
}

const Page = styled.div`
  min-height: 100vh;
  background: ${p => p.theme.palette.background.default};
  font-family: ${p => p.theme.typography.fontFamily};
  display: flex;
  flex-direction: column;
  padding: 0 16px 32px;
  box-sizing: border-box;
`;

const TopBar = styled.div`
  flex-shrink: 0;
`;

const Subtitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.theme.palette.text.secondary};
  margin: 4px 0 16px;
  padding: 0 4px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const ParcelCard = styled.div`
  background: ${p => p.theme.palette.background.paper};
  border: 1px solid ${p => p.theme.palette.border.main};
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  gap: 10px;
  box-shadow: ${p => p.theme.shadows[1]};
`;

const ImageBox = styled.div`
  width: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 10px;
  overflow: hidden;
  background: ${p => p.theme.palette.background.default};

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

const CardDescription = styled.p`
  font-size: 11px;
  font-weight: 500;
  color: ${p => p.theme.palette.text.secondary};
  text-align: center;
  margin: 0;
  line-height: 1.4;
  font-style: italic;
  min-height: 2.8em;
`;

const TutorialBtn = styled.button`
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  background: ${p => p.theme.palette.accent.main};
  border: none;
  color: #FFFFFF;
  font-size: 13px;
  font-weight: 700;
  font-family: ${p => p.theme.typography.fontFamily};
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover { opacity: 0.9; }
  &:active { opacity: 0.8; }
`;
